/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import CopyableValue from '@shared/components/CopyableValue';
import { ReactComponent as IconEdit } from '../../../../../shared/assets/Icons/IconEdit_KE.svg';
import { ReactComponent as IconCheck } from '../../../../../shared/assets/Icons/IconCheckSquared.svg';
import { ReactComponent as IconCancel } from '../../../../../shared/assets/Icons/IconCrossSquare.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconRefresh } from '../../../../../shared/assets/Icons/IconRefreshV2.svg';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import IAMPermissionsModal from '../../ReflectorDetails/IAMPermisions/IAMPermissionsModal';
import ReflectorsAssociated from './ClusterReflector/ReflectorsAssociated';
import BgpDetailsPage from '../../BgpDetails/BgpDetailsPage';
import CloudPropagationPage from '../../CloudPropagation/CloudPropagationPage';
import { clusterCloudPropagationColumns } from './ClusterCloudPropagation/ClusterCPData';
import { IAMPermission } from '../../ReflectorDetails/IAMPermisions/IAMPermisionsModalData';
import {  getClusterById, updateClusterName, updateClusterTags } from '../request/request';
import { getCloudPropagationRulesByCluster } from '../request/request';
import { useAuth0 } from '@auth0/auth0-react';
import { ClusterDetailItem, getClusterDetailsSection, clusterCloudElementsColumns } from './ClusterDetailsData';
import { format } from 'date-fns';

// Define the interface for cluster details


const TABS: TabOption[] = [
  { label: 'Details', value: 'Details' },
  { label: 'Reflectors', value: 'Reflectors' },
  { label: 'BGP', value: 'BGP' },
  { label: 'Cloud Propagation', value: 'Cloud Propagation' },
];

function getIamTagState(permissions: IAMPermission[]) {
  if (permissions.length === 0) return { label: '-', variant: 'neutral' };
  const allAllowed = permissions.every(p => p.enabled);
  const allDenied = permissions.every(p => !p.enabled);
  if (allAllowed) return { label: 'Allowed', variant: 'success' };
  if (allDenied) return { label: 'Denied', variant: 'error' };
  return { label: 'Degraded', variant: 'warning' };
}

function ClusterDetailsPage() {
  const [activeTab, setActiveTab] = useState<string>('Details');
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [isIamModalOpen, setIsIamModalOpen] = useState(false);
  const [iamPermissions, setIamPermissions] = useState<IAMPermission[]>([]);
  const [iamPermissionsLoading, setIamPermissionsLoading] = useState(false);
  const [iamPermissionsError, setIamPermissionsError] = useState<string | null>(null);
  const [cloudElementsFilters, setCloudElementsFilters] = useState<ParsedFilter[]>([]);
  const [routerId2, setRouterId2] = useState('192.168.1.2');
  const [cloudPropagationRules, setCloudPropagationRules] = useState<any[]>([]);
  const [cloudPropagationLoading, setCloudPropagationLoading] = useState(false);
  const [cloudPropagationError, setCloudPropagationError] = useState<string | null>(null);
  const [cluster, setCluster] = useState<any>(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [cloudElements, setCloudElements] = useState<any[]>([]);
  const { id: clusterId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  // Function to refresh cluster details
  const refreshClusterDetails = async () => {
    if (!clusterId) return;
    
    setClusterLoading(true);
    setClusterError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await getClusterById(token, clusterId);
      // Format lastUpdate for each instance
      const formattedCluster = {
        ...res.data,
        instances: Array.isArray(res.data.instances)
          ? res.data.instances.map((inst: any) => ({
              ...inst,
              lastUpdate: inst.lastUpdate ? format(new Date(inst.lastUpdate), 'M/d/yyyy, h:mm:ss a') : '-',
            }))
          : [],
      };
      setCluster(formattedCluster);
      setCloudElements(res.data.cloudElements || []);
    } catch (err) {
      setClusterError('Failed to load cluster details');
    } finally {
      setClusterLoading(false);
    }
  };

  // Mock fetch for IAM permissions (replace with real fetch if/when available)
  const fetchIamPermissions = async () => {
    setIamPermissionsLoading(true);
    setIamPermissionsError(null);
    try {
      // Simulate network delay
      await new Promise(res => setTimeout(res, 500));
      setIamPermissions([
        { id: 1, permission: 'cluster.read', enabled: true },
        { id: 2, permission: 'cluster.write', enabled: false },
      ]);
    } catch (err) {
      setIamPermissionsError('Failed to load IAM permissions');
    } finally {
      setIamPermissionsLoading(false);
    }
  };

  useEffect(() => {
    if (clusterId) {
      fetchIamPermissions();
    }
  }, [clusterId]);

  useEffect(() => {
    const fetchCluster = async () => {
      setClusterLoading(true);
      setClusterError(null);
      try {
        if (!clusterId) return;
        const token = await getAccessTokenSilently();
        const res = await getClusterById(token, clusterId);
        // Format lastUpdate for each instance
        const formattedCluster = {
          ...res.data,
          instances: Array.isArray(res.data.instances)
            ? res.data.instances.map((inst: any) => ({
                ...inst,
                lastUpdate: inst.lastUpdate ? format(new Date(inst.lastUpdate), 'M/d/yyyy, h:mm:ss a') : '-',
              }))
            : [],
        };
        setCluster(formattedCluster);
        setCloudElements(res.data.cloudElements || []);
      } catch (err) {
        setClusterError('Failed to load cluster details');
      } finally {
        setClusterLoading(false);
      }
    };
    if (clusterId) fetchCluster();
  }, [clusterId, getAccessTokenSilently]);

  useEffect(() => {
    if (cluster) {
      setFieldValues({
        Name: cluster.name || '',
        Tag: cluster.tags || '',
        Description: cluster.description || '',
      });
    }
  }, [cluster]);

  useEffect(() => {
    const fetchClusterPropagationRules = async () => {
      setCloudPropagationLoading(true);
      setCloudPropagationError(null);
      try {
        const token = await getAccessTokenSilently();
        const res = await getCloudPropagationRulesByCluster(token, Number(clusterId));
        setCloudPropagationRules(res.data);
      } catch (err) {
        setCloudPropagationError('Failed to load Cloud Propagation rules');
      } finally {
        setCloudPropagationLoading(false);
      }
    };
    if (clusterId) {
      fetchClusterPropagationRules();
    }
  }, [clusterId, getAccessTokenSilently]);

  // Refresh cloud propagation rules when switching to Cloud Propagation tab
  useEffect(() => {
    const fetchClusterPropagationRules = async () => {
      if (activeTab === 'Cloud Propagation' && clusterId && getAccessTokenSilently) {
        setCloudPropagationLoading(true);
        setCloudPropagationError(null);
        try {
          const token = await getAccessTokenSilently();
          const res = await getCloudPropagationRulesByCluster(token, Number(clusterId));
          setCloudPropagationRules(res.data);
        } catch (err) {
          setCloudPropagationError('Failed to load Cloud Propagation rules');
        } finally {
          setCloudPropagationLoading(false);
        }
      }
    };
    
    fetchClusterPropagationRules();
  }, [activeTab, clusterId, getAccessTokenSilently]);


  // Save handler for editable fields
  const handleSaveField = async (field: string) => {
    if (!clusterId) return;
    try {
      const token = await getAccessTokenSilently();
      if (field === 'Name') {
        await updateClusterName(token, Number(clusterId), { name: fieldValues['Name'] });
        setCluster((prev: any) => prev ? { ...prev, name: fieldValues['Name'] } : prev);
      }
      if (field === 'Tag') {
        await updateClusterTags(token, Number(clusterId), { tags: fieldValues['Tag'] });
        setCluster((prev: any) => prev ? { ...prev, tags: fieldValues['Tag'] } : prev);
      }
      setIsEditing(prev => ({ ...prev, [field]: false }));
    } catch (error) {
      setIsEditing(prev => ({ ...prev, [field]: false }));
    }
  };

  // Render function for each item
  const renderDetailItem = (item: ClusterDetailItem) => {
    // Editable fields
    if (item.type === 'editable') {
      const isFieldEditing = isEditing[item.label];
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center space-x-2 w-1/2 px-4 justify-end">
            {isFieldEditing ? (
              <>
                <GenericButton
                  onClick={() => handleSaveField(item.label)}
                  icon={<IconCheck className="w-5 h-5 text-semantic-text-secondary" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Save ${item.label}`}
                />
                <GenericButton
                  onClick={() => setIsEditing((prev) => ({ ...prev, [item.label]: false }))}
                  icon={<IconCancel className="w-5 h-5 text-semantic-text-secondary" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Cancel Edit ${item.label}`}
                />
                <input
                  type="text"
                  value={fieldValues[item.label]}
                  onChange={e => setFieldValues((prev) => ({ ...prev, [item.label]: e.target.value }))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                />
              </>
            ) : (
              <>
                <GenericButton
                  onClick={() => setIsEditing((prev) => ({ ...prev, [item.label]: true }))}
                  icon={<IconEdit className="w-4 h-4" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Edit ${item.label}`}
                />
                {item.label === 'Tag' ? (
                  <GenericTag label={fieldValues['Tag'] || '-'} variant="neutral" />
                ) : (
                  <span className="text-sm text-semantic-text-secondary">{fieldValues[item.label]}</span>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
    // Tag fields
    if (item.type === 'tag') {
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center w-1/2 px-4 justify-end">
            <GenericTag label={String(item.value)} variant={item.variant as any} />
          </div>
        </div>
      );
    }
    // IAM Permissions
    if (item.type === 'iam') {
      const { label: iamTagLabel, variant: iamTagVariant } = getIamTagState(iamPermissions);
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center w-1/2 px-4 justify-end gap-2">
            <span className="cursor-pointer hover:opacity-80" onClick={() => setIsIamModalOpen(true)}>
              <GenericTag label={iamTagLabel} variant={iamTagVariant as any} />
            </span>
            {/* <GenericButton
              icon={<IconRefresh className="w-4 h-4" />}
              label="Sync"
              variant="refresh"
              shape="ghost"
              size="sm"
              aria-label="Refresh IAM Permissions"
              onClick={fetchIamPermissions}
            /> */}
            <IAMPermissionsModal
              isOpen={isIamModalOpen}
              onClose={() => setIsIamModalOpen(false)}
              permissions={iamPermissions}
              loading={iamPermissionsLoading}
              error={iamPermissionsError}
            />
          </div>
        </div>
      );
    }
    // Default (plain text)
    return (
      <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
        <div className="flex items-center w-1/2 px-4 justify-end">
          {/* Use CopyableValue for ID fields */}
          {(item.label === 'ID' || item.label === 'VPC/VNET/VCN') ? (
            <CopyableValue value={String(item.value)} />
          ) : (
            <span className="text-sm text-semantic-text-secondary">{item.value}</span>
          )}
        </div>
      </div>
    );
  };

  // Filtered cloud elements (placeholder logic)
  const filteredCloudElements = cloudElements.filter(row => {
    if (cloudElementsFilters.length === 0) return true;
    return cloudElementsFilters.every(filter => {
      if (filter.key) {
        const key = filter.key.replace(/\s+/g, '').toLowerCase();
        const value = filter.value.trim().toLowerCase();
        switch (key) {
          case 'type':
            return row.elementType?.toLowerCase().includes(value);
          case 'elementid':
            return row.elementId?.toLowerCase().includes(value);
          case 'vpc':
            return row.vpcId?.toLowerCase().includes(value);
          case 'region':
            return row.region?.toLowerCase().includes(value);
          case 'tags':
            const tags = typeof row.tags === 'string' ? row.tags : JSON.stringify(row.tags);
            return tags?.toLowerCase().includes(value);
          case 'discoveredat':
            return row.discoveredAt?.toLowerCase().includes(value);
          case 'discoveredby':
            return row.reflectorInstance?.name?.toLowerCase().includes(value);
          default:
            return false;
        }
      } else {
        const val = filter.value.toLowerCase();
        return (
          row.elementType?.toLowerCase().includes(val) ||
          row.elementId?.toLowerCase().includes(val) ||
          row.vpcId?.toLowerCase().includes(val) ||
          row.region?.toLowerCase().includes(val) ||
          (typeof row.tags === 'string' ? row.tags : JSON.stringify(row.tags))?.toLowerCase().includes(val) ||
          row.discoveredAt?.toLowerCase().includes(val) ||
          row.reflectorInstance?.name?.toLowerCase().includes(val)
        );
      }
    });
  });

  // Use the extracted function to get the details section
  const detailsSection = cluster ? getClusterDetailsSection(cluster) : [];

  // Split items for two columns (using real data)
  const mid = detailsSection[0] ? Math.ceil(detailsSection[0].items.length / 2) : 0;
  const leftColumnItems = detailsSection[0] ? detailsSection[0].items.slice(0, mid) : [];
  const rightColumnItems = detailsSection[0] ? detailsSection[0].items.slice(mid) : [];

  // Refresh function for details and IAM permissions
  const handleRefreshDetails = async () => {
    if (!clusterId) return;
    
    setClusterLoading(true);
    try {
      // Refresh cluster details
      await refreshClusterDetails();
      // Refresh IAM permissions
      await fetchIamPermissions();
    } catch (error) {
      console.error('Error refreshing details:', error);
    } finally {
      setClusterLoading(false);
    }
  };

  // Remove the complex flattening logic since cluster API should return the same format
  // const flatCloudPropagationRules = ...

  return (
    <div className="relative">
      {clusterLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
        </div>
      )}
      {clusterError && (
        <div className="text-red-500 text-center py-4">{clusterError}</div>
      )}
      {!clusterLoading && !clusterError && cluster && (
        <>
          <div className="mb-4 flex items-center gap-2">
            <GenericButton
              label="Back"
              variant="secondary"
              shape="ghost"
              size="sm"
              iconLeftName="arrowLeft"
              onClick={() => navigate('/reflector-v2/management/clusters')}
            />
          </div>
          <div className="flex items-center justify-start mb-2 max-w-[800px] ">
            <GenericTabs
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
          {activeTab === 'Details' && (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 auto-rows-auto">
              {/* Details Card */}
              <GenericCard isCollapsible={false} headerContent={
                <div className="flex items-center justify-between w-full">
                  <span>Details</span>
                  <GenericButton
                    icon={<IconRefresh className="w-4 h-4" />}
                    label="Sync"
                    variant="refresh"
                    shape="ghost"
                    size="sm"
                    aria-label="Refresh Details"
                    onClick={handleRefreshDetails}
                  />
                </div>
              }>
                <div className="flex gap-8">
                  <div className="flex-1 divide-y divide-gray-100">
                    {leftColumnItems.map(renderDetailItem)}
                  </div>
                  <div className="flex-1 divide-y divide-gray-100">
                    {rightColumnItems.map(renderDetailItem)}
                  </div>
                </div>
              </GenericCard>
              {/* Cloud Elements Discovered Card */}
              <GenericCard isCollapsible={false} headerContent="Cloud Elements Discovered">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex-1 p-2">
                    <GenericSearchbar
                      placeholder="Search cloud elements..."
                      suggestedKeys={["Type", "Element ID", "VPC", "Region", "Tags", "Discovered At", "Discovered By"]}
                      onFiltersChange={setCloudElementsFilters}
                    />
                  </div>
                </div>
                <GenericTable
                  columns={clusterCloudElementsColumns}
                  data={filteredCloudElements}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                />
              </GenericCard>
            </div>
          )}
          {activeTab === 'Reflectors' && (
            <GenericCard isCollapsible={false} headerContent="Reflectors">
              <div className="flex flex-col gap-6">
                <ReflectorsAssociated reflectors={cluster.instances || []} clusterId={clusterId} onRoleChange={refreshClusterDetails} />
              </div>
            </GenericCard>
          )}
          {activeTab === 'BGP' && (
            <BgpDetailsPage
              config={{
                ...cluster?.bgpConfig,
                clusterReflectors: cluster?.instances?.map((inst: { id: number }) => inst.id) || [],
                clusterReflectorNames: cluster?.instances?.map((inst: { name: string }) => inst.name) || [],
                clusterInterfaceIds: cluster?.instances?.map((inst: { interfaceId: number }) => inst.interfaceId) || [],
                localAs: cluster?.bgpConfig?.localAs || '',
                routerId: cluster?.bgpConfig?.routerId || '',
              }}
              clusterId={clusterId}
              getAccessTokenSilently={getAccessTokenSilently}
              showReflector2
              routerId2={routerId2}
              onRouterId2Change={setRouterId2}
              context="cluster"
            />
          )}
          {activeTab === 'Cloud Propagation' && (
            <CloudPropagationPage
              data={cloudPropagationRules}
              columns={clusterCloudPropagationColumns}
              error={cloudPropagationError}
              getAccessTokenSilently={getAccessTokenSilently}
              clusterId={clusterId ? Number(clusterId) : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}

export default ClusterDetailsPage; 